import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Supported languages
export const SUPPORTED_LANGUAGES = ['pt', 'en', 'it', 'de', 'fr', 'es'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  pt: 'Português',
  en: 'English',
  it: 'Italiano',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
};

// Default and fallback language
const DEFAULT_LANGUAGE = 'en';
const FALLBACK_LANGUAGE = 'en';

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    defaultNS: 'common',
    ns: ['common'],

    detection: {
      // Order of language detection methods
      order: ['navigator', 'localStorage', 'querystring', 'htmlTag'],
      // Cache detected language in localStorage
      caches: ['localStorage'],
      // Lookup query parameter name
      lookupQuerystring: 'lang',
      // Lookup localStorage key
      lookupLocalStorage: 'nis2_language',
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    react: {
      useSuspense: true,
    },

    debug: process.env.NODE_ENV === 'development',
  });

// Helper function to change language
export const changeLanguage = async (lang: SupportedLanguage): Promise<void> => {
  await i18n.changeLanguage(lang);
  localStorage.setItem('nis2_language', lang);
  document.documentElement.lang = lang;
};

// Helper function to get current language
export const getCurrentLanguage = (): SupportedLanguage => {
  const lang = i18n.language?.split('-')[0] as SupportedLanguage;
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
};

export default i18n;
